import React, { useState, useRef, useEffect } from 'react';
import { FileText, ChevronDown, Loader } from 'lucide-react';
import apiService from '../../services/apiService';
import { downloadPDFFromHTML } from '../../utils/pdfGenerator';
import {
  buildAccountOverviewHTML,
  buildPaymentReportHTML,
  buildMemberStatementHTML,
  buildAnnualSummaryHTML,
  buildOverdueLetterHTML,
  buildWelcomeLetterHTML,
} from '../../utils/pdfReports';

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 10 }, (_, i) => CURRENT_YEAR - i);

export default function ReportsDropdown({ member, family, payments }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(null); // { type, data }
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const generate = async (type, opts = {}) => {
    setOpen(false);
    setDialog(null);
    setLoading(true);
    try {
      let html;
      const ref = member.membershipRef || member.id;

      // Fetch payments fresh if not passed
      const pays = payments?.length > 0 ? payments : await apiService.getSubscriptionPayments(member.id).catch(() => []);
      const fam  = family?.length > 0   ? family   : await apiService.getFamilyMembers(member.id).catch(() => []);

      switch (type) {
        case 'account_overview':
          html = buildAccountOverviewHTML(member, fam, pays);
          await downloadPDFFromHTML(html, `Account_Overview_${ref}`);
          break;
        case 'payment_grid':
          html = buildPaymentReportHTML(member, pays);
          await downloadPDFFromHTML(html, `Payment_Report_${ref}`);
          break;
        case 'statement':
          html = buildMemberStatementHTML(member, pays, opts.dateFrom, opts.dateTo);
          await downloadPDFFromHTML(html, `Statement_${ref}`);
          break;
        case 'annual':
          html = buildAnnualSummaryHTML(member, pays, opts.year || CURRENT_YEAR);
          await downloadPDFFromHTML(html, `Annual_Summary_${ref}_${opts.year || CURRENT_YEAR}`);
          break;
        case 'overdue_letter': {
          const result = buildOverdueLetterHTML(member, fam, pays, opts.fromMonth, opts.toMonth);
          await downloadPDFFromHTML(result.html, `Overdue_Letter_${ref}`);
          if (result.overdueData?.totalOverdue > 0) {
            const { totalOverdue, overdueMonths } = result.overdueData;
            const period = `${opts.fromMonth || 'start'} to ${opts.toMonth || 'now'}`;
            const noteContent = `Overdue letter generated. Period: ${period}. Total outstanding: £${totalOverdue.toFixed(2)} (${overdueMonths.length} month${overdueMonths.length !== 1 ? 's' : ''}).`;
            await apiService.createMemberNote(member.id, {
              note_type: 'Letter',
              content: noteContent,
              note_date: new Date().toISOString().slice(0, 10),
            }).catch(() => {});
          }
          break;
        }
        case 'welcome':
          html = buildWelcomeLetterHTML(member, fam);
          await downloadPDFFromHTML(html, `Welcome_Letter_${ref}`);
          break;
        default: break;
      }
    } catch (err) {
      alert(err.message || 'Failed to generate PDF');
    } finally {
      setLoading(false);
    }
  };

  const items = [
    { label: 'Account Overview',    key: 'account_overview', desc: 'Full account with financials' },
    { label: 'Payment Record Grid', key: 'payment_grid',     desc: 'Year-by-year payment matrix' },
    { label: 'Account Statement',   key: 'statement',        desc: 'Date-range transaction list', hasDialog: true },
    { label: 'Annual Summary',      key: 'annual',           desc: 'Monthly breakdown for a year', hasDialog: true },
    { label: 'Overdue Letter',      key: 'overdue_letter',   desc: 'Formal overdue payment letter', hasDialog: true },
    { label: 'Welcome Letter',      key: 'welcome',          desc: 'Membership welcome letter' },
  ];

  const handleItem = (item) => {
    if (item.hasDialog) {
      setDialog({ type: item.key, dateFrom: '', dateTo: '', fromMonth: '', toMonth: '', year: CURRENT_YEAR });
      setOpen(false);
    } else {
      generate(item.key);
    }
  };

  const DialogTitle = {
    statement:     'Account Statement — Date Range',
    annual:        'Annual Summary — Select Year',
    overdue_letter:'Overdue Letter — Month Range',
  }[dialog?.type] || '';

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        disabled={loading}
        className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg text-sm font-medium transition-colors shadow-sm disabled:opacity-50">
        {loading
          ? <><Loader className="w-4 h-4 animate-spin" /> Generating…</>
          : <><FileText className="w-4 h-4" /> Reports <ChevronDown className="w-3 h-3" /></>
        }
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-60 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden">
          <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Generate Report</p>
          </div>
          {items.map(item => (
            <button key={item.key} onClick={() => handleItem(item)}
              className="w-full text-left px-4 py-2.5 hover:bg-blue-50 transition-colors border-b border-slate-50 last:border-0">
              <p className="text-sm font-medium text-slate-800">{item.label}</p>
              <p className="text-xs text-slate-400">{item.desc}</p>
            </button>
          ))}
        </div>
      )}

      {/* Dialog overlay */}
      {dialog && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl p-6 w-80 mx-4">
            <h3 className="font-semibold text-slate-800 mb-4 text-sm">{DialogTitle}</h3>
            <div className="space-y-3">
              {dialog.type === 'statement' && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">From (YYYY-MM)</label>
                    <input type="month" value={dialog.dateFrom}
                      onChange={e => setDialog(d => ({ ...d, dateFrom: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">To (YYYY-MM)</label>
                    <input type="month" value={dialog.dateTo}
                      onChange={e => setDialog(d => ({ ...d, dateTo: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                </>
              )}
              {dialog.type === 'annual' && (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Year</label>
                  <select value={dialog.year}
                    onChange={e => setDialog(d => ({ ...d, year: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              )}
              {dialog.type === 'overdue_letter' && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">From Month</label>
                    <input type="month" value={dialog.fromMonth}
                      onChange={e => setDialog(d => ({ ...d, fromMonth: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">To Month</label>
                    <input type="month" value={dialog.toMonth}
                      onChange={e => setDialog(d => ({ ...d, toMonth: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                </>
              )}
            </div>
            <div className="flex gap-2 mt-4 justify-end">
              <button onClick={() => setDialog(null)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-lg transition-colors">
                Cancel
              </button>
              <button onClick={() => generate(dialog.type, dialog)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                Generate PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
